import { Button, Checkbox, FormControlLabel } from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { classes } from "portal/theme/theme";
import { ConfigType } from "protos/config/api/config_service";
import { CropEditor } from "portal/components/CropEditor";
import { DisplayCrop, getConfigKey } from "portal/utils/crops";
import {
  emptyRowSelectionModel,
  hasRowSelection,
  rowSelectionModelToArray,
} from "portal/utils/gridRowSelection";
import { findWhere } from "portal/utils/arrays";
import { getCropPath, getRobotPath, RobotSubpath } from "portal/utils/routing";
import {
  GridActionsColDef,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { isUndefined } from "portal/utils/identity";
import { ModelName } from "portal/components/ModelName";
import { NoScroll } from "portal/components/Page";
import { portalApi } from "portal/state/portalApi";
import { SearchField } from "portal/components/header/SearchField";
import {
  useCanPinModels,
  useCanReadAdvancedCrops,
  useCanUpdateAdvancedCrops,
} from "portal/views/robots/crops/permissions";
import { useFuzzySearch } from "portal/utils/hooks/useFuzzySearch";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useSortedCrops } from "portal/utils/hooks/useCrops";
import { useTranslation } from "react-i18next";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import DisableIcon from "@mui/icons-material/CloseOutlined";
import EnableIcon from "@mui/icons-material/CheckOutlined";
import PinnedIcon from "@mui/icons-material/PushPinOutlined";
import React, { FunctionComponent, useMemo, useState } from "react";

const defaultColumn: Partial<GridColDef> = {
  sortable: true,
  disableColumnMenu: true,
  headerClassName: "capitalize",
};

const _RobotVeselkaCropList: FunctionComponent = () => {
  const { serial } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const canReadAdvancedCrops = useCanReadAdvancedCrops();
  const canEnableCrops = useCanUpdateAdvancedCrops();
  const canPinModels = useCanPinModels();
  const canOpenCrop = canReadAdvancedCrops || canEnableCrops || canPinModels;

  const { sortedCrops: crops, isFetching } = useSortedCrops({ serial });

  const [showArchived, setShowArchived] = useState<boolean>(false);
  const allCrops = useMemo(() => {
    if (!canEnableCrops) {
      return crops?.enabled;
    }
    return showArchived
      ? [...(crops?.all ?? []), ...(crops?.archived ?? [])]
      : crops?.all;
  }, [canEnableCrops, crops, showArchived]);

  // selected crop
  const { pathname } = useLocation();
  let cropId: string = "";
  const basePath = getRobotPath(serial, RobotSubpath.CROPS);
  if (!pathname.endsWith(basePath)) {
    cropId = pathname.replace(`${basePath}/`, "");
  }
  const selectedCrop = findWhere(allCrops, { id: cropId });

  const { searchText, setSearchText, results } = useFuzzySearch<DisplayCrop>({
    items: allCrops ?? [],
    options: {
      ignoreLocation: true,
      keys: ["id", "localeName", "pinned", "recommended"],
    },
  });

  const columns: Array<GridColDef<DisplayCrop> | GridActionsColDef> = [
    {
      ...defaultColumn,
      field: "commonName",
      headerName: t("models.crops.crop_one"),
      cellClassName: () => "font-bold",
      valueGetter: (value, crop) => crop.localeName,
      width: 250,
    },
    {
      ...defaultColumn,
      field: "id",
      headerName: t("models.crops.fields.id"),
      cellClassName: () => "font-mono text-xs/inherit",
      width: 260,
    },
    {
      ...defaultColumn,
      field: "isEnabled",
      headerName: t("utils.descriptors.enabled"),
      width: 100,
      valueGetter: (value, crop) =>
        crop.isEnabled
          ? t("utils.descriptors.enabled")
          : t("utils.descriptors.disabled"),
      cellClassName: ({ row: crop }) =>
        classes("text-center", {
          "font-bold": crop.isEnabled,
          "text-green-500": crop.isEnabled,
          "text-gray-400": !crop.isEnabled,
        }),
    },
    {
      ...defaultColumn,
      field: "pinned",
      headerName: t("models.crops.fields.pinned"),
      width: 250,
      cellClassName: ({ row: crop }) =>
        classes({
          "text-yellow-500": crop.isEnabled,
        }),
      renderCell: ({ row: crop }) => {
        if (!crop.pinned) {
          return;
        }
        if (!canPinModels) {
          return (
            <span className="flex h-full items-center">
              <PinnedIcon />
            </span>
          );
        }
        return (
          <ModelName modelId={crop.pinned}>
            <span className="font-mono text-xs/inherit cursor-pointer text-yellow-500">
              {crop.pinned}
            </span>
          </ModelName>
        );
      },
    },
    {
      ...defaultColumn,
      field: "recommended",
      headerName: t("models.crops.fields.recommended"),
      width: 250,
      cellClassName: ({ row: crop }) =>
        classes("text-mono text-xs/inherit", {
          "text-red-500": crop.pinned && crop.pinned !== crop.recommended,
        }),
      renderCell: ({ row: crop }) => (
        <ModelName modelId={crop.recommended}>{crop.recommended}</ModelName>
      ),
    },
  ];

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>(
    emptyRowSelectionModel()
  );

  const [setConfigValue] = useMutationPopups(
    portalApi.endpoints.setConfigValue.useMutation(),
    {
      success: capitalize(
        t("utils.actions.savedLong", {
          subject: capitalize(t("models.configs.config_one")),
        })
      ),
    }
  );

  const [disabledLoading, setDisabledLoading] = useState<boolean>(false);
  const [enabledLoading, setEnabledLoading] = useState<boolean>(false);
  const toggleSelected = async (enabled: boolean): Promise<void> => {
    if (!serial) {
      return;
    }
    const promises: Promise<any>[] = [];
    for (const id of rowSelectionModelToArray(
      selectedIds,
      results.map((crop) => crop.id)
    ).filter((id) => {
      // allow disabling of all crops
      if (!enabled) {
        return true;
      }
      // only allow enabling of non-archived crops
      const archivedCrop = findWhere(crops?.archived, { id });
      return !archivedCrop;
    })) {
      promises.push(
        setConfigValue({
          serial,
          path: [getConfigKey(serial, String(id)), "enabled"].join("/"),
          value: enabled,
          reason: `Crop ${enabled ? "enabled" : "disabled"}`,
          configType: ConfigType.BOOL,
        })
      );
    }
    await Promise.all(promises);
    setSelectedIds(emptyRowSelectionModel());
  };

  return (
    <NoScroll>
      <CarbonDataGrid<DisplayCrop>
        header={
          <>
            <SearchField
              value={searchText}
              onChange={setSearchText}
              label={t("utils.actions.searchLong", {
                subject: capitalize(
                  t("models.crops.crop", { count: crops?.all.length ?? 0 })
                ),
              })}
            />
            {canEnableCrops && (
              <div className="flex gap-2">
                <FormControlLabel
                  className="flex-shrink-0"
                  control={
                    <Checkbox
                      checked={showArchived}
                      color="default"
                      onChange={(event, checked) => setShowArchived(checked)}
                    />
                  }
                  label={
                    <span className="whitespace-nowrap">
                      <span className="hidden sm:inline">
                        {t("utils.actions.showLong", {
                          subject: t(
                            "models.crops.fields.confidence.values.archived"
                          ),
                        })}
                      </span>
                      <span className="sm:hidden">
                        {t("models.crops.fields.confidence.values.archived")}
                      </span>
                    </span>
                  }
                />
                <Button
                  variant="contained"
                  color="error"
                  loading={disabledLoading}
                  disabled={enabledLoading || !hasRowSelection(selectedIds)}
                  onClick={async () => {
                    setDisabledLoading(true);
                    await toggleSelected(false);
                    setDisabledLoading(false);
                  }}
                  startIcon={<DisableIcon />}
                >
                  <span className="hidden sm:inline">
                    {t("utils.actions.disableLong", {
                      subject: t("utils.table.selected"),
                    })}
                  </span>
                  <span className="sm:hidden">{t("utils.table.selected")}</span>
                </Button>
                <Button
                  color="success"
                  variant="contained"
                  loading={enabledLoading}
                  disabled={disabledLoading || !hasRowSelection(selectedIds)}
                  onClick={async () => {
                    setEnabledLoading(true);
                    await toggleSelected(true);
                    setEnabledLoading(false);
                  }}
                  startIcon={<EnableIcon />}
                >
                  <span className="hidden sm:inline">
                    {t("utils.actions.enableLong", {
                      subject: t("utils.table.selected"),
                    })}
                  </span>
                  <span className="sm:hidden">{t("utils.table.selected")}</span>
                </Button>
              </div>
            )}
          </>
        }
        dimensionClasses={classes("h-auto flex-0", {
          "mr-80": !isUndefined(selectedCrop),
        })}
        className={classes("flex flex-1")}
        loading={isFetching}
        rows={results}
        columns={columns}
        columnVisibilityModel={{
          isEnabled: canEnableCrops,
          id: canReadAdvancedCrops,
          recommended: canReadAdvancedCrops,
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "isEnabled", sort: "desc" }],
          },
        }}
        hideFooter
        getRowId={(row: DisplayCrop) => row.id}
        getRowClassName={({ row: crop }) =>
          classes({
            "cursor-pointer": canOpenCrop,
            "bg-blue-500": crop.id === selectedCrop?.id,
          })
        }
        onRowClick={({ row: crop }) => {
          if (!canOpenCrop) {
            return;
          }
          navigate(getCropPath(serial ?? "", crop.id));
        }}
        disableRowSelectionOnClick
        checkboxSelection={canEnableCrops}
        onRowSelectionModelChange={(newRowSelectionModel) =>
          setSelectedIds(newRowSelectionModel)
        }
        rowSelectionModel={selectedIds}
      />
      <CropEditor
        serial={serial}
        configCrop={selectedCrop}
        open={!isUndefined(selectedCrop)}
        onClose={() => navigate(basePath)}
      />
    </NoScroll>
  );
};

export const RobotVeselkaCropList = withAuthenticationRequired(
  _RobotVeselkaCropList
);
